// Logic for the week dashboard - see doc/week-dashboard-concept.org.
//
// Everything here is pure so it can be unit-tested without rendering: the page
// component only arranges what these functions return.

import { List } from 'immutable';

import { createIsTodoKeywordInDoneState } from './org_utils';
import { attributedStringToRawText } from './export_org';
import { directoryOfPath, formatDatePath } from './dashboard';

export const WEEK_WINDOW_DAYS = 7;

// INFO: Local date parts throughout, never toISOString(). That formats in UTC and
// would shift the whole window by a day east of Greenwich - the same trap already
// documented in lib/dashboard.js.
const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const isSameLocalDay = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * The days of the window, oldest first, ending on `today`.
 *
 * A rolling window rather than a calendar week: it always shows seven days of
 * material, where a Monday-aligned week would show a single column on a Monday.
 * The concept notes the trade-off - the fresh-start effect favours a calendar week.
 */
export const windowOfDays = (today, count = WEEK_WINDOW_DAYS) => {
  const end = startOfLocalDay(today);
  const days = [];

  for (let offset = count - 1; offset >= 0; offset -= 1) {
    days.push(new Date(end.getFullYear(), end.getMonth(), end.getDate() - offset));
  }

  return days;
};

/** The daily-note path expected for each day of the window. */
export const dailyPathsForWindow = (dailyPathTemplate, today, count = WEEK_WINDOW_DAYS) =>
  windowOfDays(today, count).map((date) => ({
    date,
    path: formatDatePath(dailyPathTemplate, date),
  }));

/**
 * The distinct folders those paths live in.
 *
 * Usually one, but a template like '/roam/YYYY/MM-DD.org' spreads the window across
 * several, and listing only the first would silently lose days.
 */
export const dailyFoldersForWindow = (dailyPathTemplate, today, count = WEEK_WINDOW_DAYS) => {
  const folders = dailyPathsForWindow(dailyPathTemplate, today, count).map(({ path }) =>
    directoryOfPath(path)
  );

  return [...new Set(folders)];
};

/**
 * Checkbox items anywhere in a parsed description.
 *
 * Checkboxes are list items: a part with type 'list' holds `items`, each carrying
 * `isCheckbox` and `checkboxState`. Items nest further lists through their own
 * `contents`, so this has to recurse - a flat scan misses indented subitems.
 */
const collectCheckboxItems = (parts, into = []) => {
  (parts || List()).forEach((part) => {
    if (!part || typeof part.get !== 'function') return;

    if (part.get('type') === 'list') {
      (part.get('items') || List()).forEach((item) => {
        if (item.get('isCheckbox')) {
          into.push(item);
        }
        collectCheckboxItems(item.get('contents'), into);
      });
    }
  });

  return into;
};

const checkboxText = (item) => {
  try {
    return `${attributedStringToRawText(item.get('titleLine'))}`.trim();
  } catch (error) {
    // Never let one odd list item take the whole page down.
    return '';
  }
};

/**
 * Open tasks and completed count for one parsed file.
 *
 * "Open" per the concept: a headline whose todo keyword is not in a done state, and a
 * checkbox that is unchecked or partial. A plain headline or plain list item is
 * structure, not a task, and is ignored. `[X]` and done headlines feed `doneCount`,
 * which the progress bar needs - without them it under-reports.
 */
export const collectTasks = ({ headers, todoKeywordSets, path, date = null }) => {
  const isDone = createIsTodoKeywordInDoneState(todoKeywordSets || List());
  const open = [];
  let doneCount = 0;

  (headers || List()).forEach((header) => {
    const todoKeyword = header.getIn(['titleLine', 'todoKeyword']);

    if (todoKeyword) {
      if (isDone(todoKeyword)) {
        doneCount += 1;
      } else {
        open.push({
          kind: 'headline',
          key: `${path}#h${header.get('id')}`,
          path,
          date,
          todoKeyword,
          text: `${header.getIn(['titleLine', 'rawTitle']) || ''}`.trim(),
          hasPlanning: (header.get('planningItems') || List()).size > 0,
        });
      }
    }

    collectCheckboxItems(header.get('description')).forEach((item) => {
      const checkboxState = item.get('checkboxState');

      if (checkboxState === 'checked') {
        doneCount += 1;
        return;
      }

      open.push({
        kind: 'checkbox',
        key: `${path}#c${item.get('id')}`,
        path,
        date,
        checkboxState,
        text: checkboxText(item),
        hasPlanning: false,
      });
    });
  });

  return { open, doneCount };
};

/**
 * Fold per-file results into what the page renders.
 *
 * `files` is [{ path, date, open, doneCount }] - date null for gtd.org, a Date for a
 * daily note. A day with no file is absent from `files` entirely, and the strip must
 * render that as "no note" rather than as a zero; see the concept, §4.3.
 */
export const summarizeWeek = ({ files, days }) => {
  const collected = files || [];

  const dayColumns = (days || []).map((date) => {
    const file = collected.find((candidate) => isSameLocalDay(candidate.date, date));

    return {
      date,
      hasNote: !!file,
      openCount: file ? file.open.length : 0,
      doneCount: file ? file.doneCount : 0,
    };
  });

  const totals = collected.reduce(
    (accumulator, file) => ({
      open: accumulator.open + file.open.length,
      done: accumulator.done + file.doneCount,
    }),
    { open: 0, done: 0 }
  );

  return {
    dayColumns,
    openCount: totals.open,
    doneCount: totals.done,
    // Days that actually have a note, so "6 days in" does not count days off.
    daysWithNotes: dayColumns.filter((column) => column.hasNote).length,
  };
};

/**
 * Consecutive days with at least one completion, counted back from the newest day.
 *
 * A day with no note does not break the run - it is a day off, not a failure. That is
 * the "grace" the concept asks for (§4.1): loss aversion supplies the pull, without
 * one quiet Sunday ending the practice.
 */
export const currentRun = (dayColumns) => {
  let run = 0;

  for (let index = (dayColumns || []).length - 1; index >= 0; index -= 1) {
    const column = dayColumns[index];

    if (column.doneCount > 0) {
      run += 1;
    } else if (column.hasNote) {
      break;
    }
  }

  return run;
};

/** A sensible default target: what was actually completed, rounded up to something round. */
export const suggestedWeeklyTarget = (doneCount) => Math.max(5, Math.ceil((doneCount || 0) / 5) * 5);

/**
 * Order tasks for the focus dashboard's "what are you working on?" picker:
 * today's own tasks first, then the rest of the week newest day first (a fresher
 * carry-over is more likely to still matter than an older one, and working
 * backwards mirrors how someone would recall "what was I doing yesterday, the
 * day before..."), then the standing GTD list last - its items are not boxed to
 * any one day, so they trail rather than competing with what today and the week
 * already surfaced. Order within a day, and within GTD, is left exactly as
 * collected (headline order in the file).
 */
export const orderTasksForPicker = (tasks, today) => {
  const todays = [];
  const week = [];
  const gtd = [];

  (tasks || []).forEach((task) => {
    if (!task.date) {
      gtd.push(task);
    } else if (isSameLocalDay(task.date, today)) {
      todays.push(task);
    } else {
      week.push(task);
    }
  });

  week.sort((a, b) => b.date.getTime() - a.date.getTime());

  return [...todays, ...week, ...gtd];
};
